---
title: Compressor
description: "A compressor device: threshold, ratio, attack, release, knee, make-up and mix, a panel that draws its transfer curve, a Web Audio engine with a sidechain, and tools for agents."
component: true
base: base
---

<ComponentPreview styleName="base-nova" name="block-compressor" />

Brings loud parts down. Above the threshold, the level rises by one decibel for every `ratio` decibels in. With the sidechain on, it follows the level of another signal, so a kick can duck a pad. It is a [device](/docs/devices): a state, a panel, an engine and tools, each its own file.

## Installation

<Install name="compressor" kind="device" />

## Controls

<DeviceControls device="compressor">
<Control id="thresholdDb">The level above which the compressor works.</Control>
<Control id="ratio">How much the level is brought down above the threshold. At 4:1, 4 dB in give 1 dB out.</Control>
<Control id="attackMs">How fast the compressor takes the gain down once the level is over the threshold.</Control>
<Control id="releaseMs">How fast the gain comes back once the level is under it.</Control>
<Control id="kneeDb">How gradually the compressor starts to work around the threshold. 0 is a hard knee.</Control>
<Control id="autoMakeup">Keeps the make-up gain the browser's node adds on its own, which grows with the threshold and the ratio.</Control>
<Control id="makeupDb">Makes up for the gain taken off. It does nothing while Auto make-up is on.</Control>
<Control id="mix">The balance of the compressed signal and the dry one, for parallel compression.</Control>
<Control id="external">Makes the compressor follow the signal connected to `engine.sidechain`. See [Sidechain](/docs/devices#sidechain).</Control>
</DeviceControls>

## Panel

The panel draws the transfer curve the state plays: the threshold, the dashed unity line, and a dot that rides the curve at the level going in. Pass `level`, a live source in dB, to show the dot. Below the threshold the curve sits above unity by the make-up gain.

<DeviceUsage name="compressor" />

## Engine

Create it, connect it and update it as for any device: see [The engine](/docs/devices#the-engine).

```ts
engine.reduction; // dB being taken off, read per frame
```

What goes on each node comes from `compressorSettings(state)`. Two things the browser's compressor node does that the engine accounts for:

- **It adds a make-up gain of its own that cannot be turned off.** It grows with the threshold and the ratio (+10.8 dB at -24 dB and 4:1). With Auto make-up off, the engine takes it back out and applies Make-up. With it on, the node's is kept.
- **It looks ahead by 6 ms.** The dry path waits as long, so a parallel mix does not comb. This is the [latency](/docs/devices#latency) of the device.

With the sidechain on, the browser's node has no input for it, so the signal goes through a gain that a worklet drives. The worklet follows the peak of the sidechain, instant up and 10 ms down, and applies the same curve as the node, with a soft knee, a slope of 1/ratio over the threshold, and the attack and release on the reduction. The make-up of the node is added by hand. Measured in Chrome with a sine at -6 dB over a threshold of -18 dB at 4:1, it takes off 8.9 dB at 1 kHz and 8.5 dB at 60 Hz, where the curve says 9. With the sidechain on, `engine.reduction` reads the detector.

## How close the curve is

The curve is the static curve of the node: the level once the gain has settled. A real detector follows a signal with an attack and a release, so what you hear sits a little above it. Measured in Chrome with a 440 Hz sine, it is about 0.5 dB at the defaults and up to 1.3 dB with a slow attack and a short release. Below the threshold it is exact.
